<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { bemBuilder, debounce } from "@chatfood/core-utils";
import {
  AtomCard,
  AtomText,
  SatInputSearch,
  SatElasticPanel,
  AtomButton,
} from "@chatfood/design-system";
import {
  BASE_TELEPORT_SELECTOR,
  useBugReporter,
  useBusinessStore,
  useDirectBackend,
} from "#imports";
import { useLocale } from "~/composables/use-locale";
import type { IDomainPaginatedList } from "~/domain/paginated-list";
import type { IDomainOutlet } from "~/domain/outlet";
import OutletDetails from "~/components/outlet-details/OutletDetails.vue";

const css = bemBuilder("page-locations");
const { businessRepo } = useDirectBackend();
const business = useBusinessStore();
const { t } = useLocale();
const { report } = useBugReporter();

const search = ref("");
const isLoading = ref(false);
const locations = ref<IDomainPaginatedList<IDomainOutlet>>();
const chosenLocation = ref<IDomainOutlet>();
const currentPage = ref(1);
const displayLoadMore = ref(true);

const showOutletDetails = ref(false);

const fetchlocations = async (value?: string) => {
  isLoading.value = true;
  try {
    const res = await businessRepo.getOutlets({
      businessId: business.id,
      salesChannel: business.salesChannel,
      search: value ?? undefined,
      perPage: 10,
      page: currentPage.value,
    });

    if (!locations.value) {
      locations.value = { ...res, data: [...res.data] };
    } else {
      locations.value = {
        ...locations.value,
        data: [...locations.value.data, ...res.data],
      };
    }

    currentPage.value = currentPage.value + 1;

    if (
      res.data.length === 0 ||
      (locations.value && locations.value.data.length === 0)
    ) {
      displayLoadMore.value = false;
    }
  } catch (e) {
    report(e);
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => search.value,
  debounce(() => {
    fetchlocations(search.value.trim() === "" ? undefined : search.value);
  }, 1000),
);

const onShowLocationDetails = (location: IDomainOutlet) => {
  if (!location) return;
  showOutletDetails.value = true;
  chosenLocation.value = location;
};

onMounted(() => {
  fetchlocations();
});
</script>

<template>
  <div :class="css()">
    <AtomCard padding="large" :class="css('card')">
      <div :class="css('wrapper')">
        <AtomText type="subtitle" color="color-text-secondary"
          >{{ t("pages.locations.title") }}
        </AtomText>
        <SatInputSearch
          :placeholder="t('pages.locations.search_placeholder')"
          :value="search"
          @keyup="(value) => (search = value)"
        />
        <div :class="css('location-wrapper')">
          <a
            v-for="location in locations?.data"
            :key="location.id"
            :class="css('location')"
            href="#"
            @click.prevent="onShowLocationDetails(location)"
          >
            <AtomText type="body-bold">
              {{ location.name }}
            </AtomText>
            <AtomText type="body" color="color-text-secondary">
              {{ location.address.location }}
            </AtomText>
          </a>
        </div>
      </div>
      <div :class="css('load-more-area')">
        <AtomButton
          v-if="displayLoadMore"
          :is-loading="isLoading"
          theme="ghost"
          @click="fetchlocations()"
        >
          {{ t("pages.locations.load_more_cta") }}
        </AtomButton>
        <AtomText v-else color="color-text-disabled">
          {{ t("pages.locations.no_more_records_message") }}
        </AtomText>
      </div>
      <Teleport :to="BASE_TELEPORT_SELECTOR">
        <SatElasticPanel
          v-model="showOutletDetails"
          show-close
          width-fit-content
        >
          <OutletDetails v-if="chosenLocation" :outlet-id="chosenLocation.id" />
        </SatElasticPanel>
      </Teleport>
    </AtomCard>
  </div>
</template>

<style scoped lang="scss">
.page-locations {
  @media (width >= $breakpoint-sm) {
    padding: var(--spacing-2xl);
  }

  &__card {
    max-width: 640px;
    margin: 0 auto;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);
  }

  &__location-wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__loading {
    display: flex;
    margin: 0 auto;
  }

  &__location {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    flex-direction: column;
    gap: var(--spacing-xs);
    border: solid 1px var(--color-base-80);
    padding: var(--spacing-md);
    border-radius: var(--radii-card-sm);
  }

  &__load-more-area {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: var(--spacing-lg);
  }
}
</style>
